import type {
  TargetingCity,
  TargetingConfig,
  TargetingCustomLocation,
  TargetingNeighborhood,
  TargetingPlace,
  TargetingRegion,
  TargetingZip,
} from '../types/metaAdDraft.js';
import {
  clampCityRadius,
  clampCustomLocationRadius,
  defaultCityRadius,
  defaultPlaceRadius,
} from './metaAdTargetingShape.js';

// Audience-builder — PURE projection of a TargetingConfig's centre+radius geo into a
// flat list the map renders (markers + radius circles). No `google` import — fully
// unit-testable. Sources:
//
//   MANUAL custom pins → structured `customLocations` (kind 'custom'; EDITABLE: move
//                        AND resize; carries its array index).
//   MANUAL cities      → `cities[]` (kind 'city'). Centre resolved separately
//                        (geoCenterResolver) + fed via resolvedGeoPoints(). Radius
//                        editable (city band); centre FIXED (the key encodes it).
//   MANUAL places      → `places[]` (kind 'place'). Like a city but the PLACE radius
//                        band (1–80 km). Circle, radius-editable, centre fixed.
//   MANUAL neighborhoods → `neighborhoods[]` (kind 'neighborhood'). PIN ONLY (Meta
//                        shows no circle) — no radius, not resizable.
//   EXTRACTED          → parsed from the verbatim `rawPayload` (Meta-native
//                        geo_locations.places/custom_locations as circles +
//                        .neighborhoods as pins, + the excluded_* equivalents → red).
//                        NOT editable (locked to the raw shape).
//
// Regions/zips/metros are area-by-key with no point geometry → never mapped.

export type GeoPointKind = 'custom' | 'city' | 'place' | 'neighborhood' | 'extracted';

// A geographic bounding box (defined here, in the pure lib, so the resolver — a
// module — imports it without a module→lib cycle).
export type GeoBounds = { north: number; south: number; east: number; west: number };

export type GeoMapPoint = {
  id: string; // stable key (React + map object identity)
  kind: GeoPointKind; // drives colour + which target field an edit writes back to
  latitude: number;
  longitude: number;
  radius: number; // in distanceUnit (NOT meters)
  distanceUnit: 'kilometer' | 'mile';
  label: string; // marker/list label
  excluded: boolean; // true → exclusion (distinct red styling)
  draggable: boolean; // marker can be dragged to MOVE the centre (custom pins only)
  editableRadius: boolean; // circle radius can be dragged (custom pins, cities, places)
  drawCircle: boolean; // false → marker only, no circle (neighborhoods)
  index: number | null; // index into targeting.customLocations (kind 'custom'); else null
  geoKey: string | null; // the targeting key for city/place/neighborhood; else null
  // SAFETY — true when this centre was geocoded FROM THE NAME and may not match the place
  // the Meta key publishes to (see GeoResolution.approximate). The map draws approximate
  // points as UNCONFIRMED (de-emphasised + warned), never as a certain location. Custom
  // pins + extracted coords are exact → false.
  approximate: boolean;
};

const M_PER_KM = 1000;
const M_PER_MILE = 1609.344;

// kyck radius (km/mi) → meters (google.maps.Circle radius is meters).
export function radiusToMeters(radius: number, unit: 'kilometer' | 'mile'): number {
  const r = Number.isFinite(radius) ? radius : 0;
  return unit === 'mile' ? r * M_PER_MILE : r * M_PER_KM;
}

// meters (read back off an edited google.maps.Circle) → kyck radius in the unit.
export function metersToRadius(meters: number, unit: 'kilometer' | 'mile'): number {
  const m = Number.isFinite(meters) ? meters : 0;
  return unit === 'mile' ? m / M_PER_MILE : m / M_PER_KM;
}

// The cache / lookup identity for a resolved geo centre: geo TYPE + KEY. Shared by
// the geo-centre resolver (which caches by it) and resolvedGeoPoints (which looks
// centres up by it). Lives here (lib) so the resolver (a module) imports it, never
// the reverse.
export function geoCacheKey(item: { type: string; key: string }): string {
  return `${item.type}:${item.key}`;
}

// Clamp a dragged radius using the bounds for THE POINT'S KIND — cities keep their
// OWN band (17 km min), places + custom pins keep the 1–80 km band. Shared map code
// must NOT apply pin bounds to cities; this is the single dispatch. Pure.
export function clampRadiusForPoint(point: Pick<GeoMapPoint, 'kind' | 'distanceUnit'>, radius: number): number {
  if (point.kind === 'city') return clampCityRadius(radius, point.distanceUnit);
  return clampCustomLocationRadius(radius, point.distanceUnit); // 'place' + 'custom' share the 1–80 band
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function coordLabel(lat: number, lng: number): string {
  return `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
}

function unitOf(v: unknown): 'kilometer' | 'mile' {
  return v === 'mile' ? 'mile' : 'kilometer';
}

// Parse one Meta-native point list (places / custom_locations / neighborhoods) into
// GeoMapPoints. `drawCircle` is false for neighborhoods (pin only). Tolerant of
// snake_case (Meta-native) AND camelCase (Zernio shape).
function parseRawPointList(
  list: unknown,
  excluded: boolean,
  idPrefix: string,
  drawCircle: boolean,
): GeoMapPoint[] {
  if (!Array.isArray(list)) return [];
  const out: GeoMapPoint[] = [];
  list.forEach((raw, i) => {
    if (!raw || typeof raw !== 'object') return;
    const r = raw as Record<string, unknown>;
    const lat = num(r.latitude);
    const lng = num(r.longitude);
    if (lat === null || lng === null) return; // no point geometry — skip (e.g. a key-only entry)
    const unit = unitOf(r.distance_unit ?? r.distanceUnit);
    const name = typeof r.name === 'string' && r.name.trim() !== '' ? r.name : null;
    out.push({
      id: `${idPrefix}-${i}`,
      kind: 'extracted',
      latitude: lat,
      longitude: lng,
      radius: drawCircle ? num(r.radius) ?? 0 : 0,
      distanceUnit: unit,
      label: name ?? coordLabel(lat, lng),
      excluded,
      draggable: false,
      editableRadius: false,
      drawCircle,
      index: null,
      geoKey: null,
      approximate: false, // extracted custom_locations carry exact Meta coords
    });
  });
  return out;
}

// Pull places (circle) + custom_locations (circle) + neighborhoods (pin) out of a
// (possibly excluded) Meta-native geo block.
function parseGeoBlock(block: unknown, excluded: boolean, idPrefix: string): GeoMapPoint[] {
  if (!block || typeof block !== 'object') return [];
  const b = block as Record<string, unknown>;
  return [
    ...parseRawPointList(b.places, excluded, `${idPrefix}-place`, true),
    ...parseRawPointList(b.custom_locations ?? b.customLocations, excluded, `${idPrefix}-custom`, true),
    ...parseRawPointList(b.neighborhoods, excluded, `${idPrefix}-nbhd`, false),
  ];
}

// Manual structured custom pins (one bucket — included OR excluded). `index` is the
// pin's index into ITS OWN array (targeting.customLocations or excluded.customLocations);
// the map's edit write-back branches on `excluded` to pick the array. Excluded pins
// render red. Pure.
function manualCustomPins(list: TargetingCustomLocation[] | undefined, excluded: boolean): GeoMapPoint[] {
  return (Array.isArray(list) ? list : []).map((c, i) => ({
    id: `${excluded ? 'exc-' : ''}custom-${i}`,
    kind: 'custom' as const,
    latitude: c.latitude,
    longitude: c.longitude,
    radius: c.radius,
    distanceUnit: c.distanceUnit === 'mile' ? 'mile' : 'kilometer',
    label: c.name && c.name.trim() !== '' ? c.name : coordLabel(c.latitude, c.longitude),
    excluded,
    draggable: true,
    editableRadius: true,
    drawCircle: true,
    index: i,
    geoKey: null,
    approximate: false, // user-placed exact coordinates
  }));
}

// The display points carrying their OWN geometry (custom pins, or extracted raw).
// Cities/places/neighborhoods are added by resolvedGeoPoints() (they need resolved
// centres). Pure.
export function geoMapPoints(targeting: TargetingConfig): GeoMapPoint[] {
  if (targeting.sourceType === 'extracted') {
    const raw = (targeting.rawPayload ?? {}) as Record<string, unknown>;
    return [
      ...parseGeoBlock(raw.geo_locations ?? raw.geoLocations, false, 'inc'),
      ...parseGeoBlock(raw.excluded_geo_locations ?? raw.excludedGeoLocations, true, 'exc'),
    ];
  }
  // Manual mode — the structured, editable custom pins (included + excluded buckets).
  return [
    ...manualCustomPins(targeting.customLocations, false),
    ...manualCustomPins(targeting.excluded?.customLocations, true),
  ];
}

// Build map points for the targeting's CITIES, PLACES and NEIGHBORHOODS, given their
// resolved centres (keyed by geoCacheKey — `${type}:${key}`). An entry with NO
// resolved centre is OMITTED (graceful: it stays a chip, isn't drawn). Centres are
// fixed (not draggable); cities/places have editable radii, neighborhoods are pins.
// A radius-0 city/place (the "Meta default" sentinel) draws at the band minimum so
// the circle is visible. Pure.
export function resolvedGeoPoints(
  geo: { cities: TargetingCity[]; places: TargetingPlace[]; neighborhoods: TargetingNeighborhood[] },
  centers: Record<string, { latitude: number; longitude: number; approximate?: boolean }>,
  opts?: { excluded?: boolean },
): GeoMapPoint[] {
  const out: GeoMapPoint[] = [];
  // Excluded points render red + carry a distinct id prefix so an excluded city and
  // an included city with the SAME key don't collide on map object identity.
  const excluded = opts?.excluded === true;
  const pfx = excluded ? 'exc-' : '';

  for (const c of geo.cities) {
    const center = centers[geoCacheKey({ type: 'city', key: c.key })];
    if (!center) continue;
    const unit = c.distanceUnit === 'mile' ? 'mile' : 'kilometer';
    out.push({
      id: `${pfx}city-${c.key}`,
      kind: 'city',
      latitude: center.latitude,
      longitude: center.longitude,
      radius: c.radius > 0 ? c.radius : defaultCityRadius(unit),
      distanceUnit: unit,
      label: c.name,
      excluded,
      draggable: false,
      editableRadius: true,
      drawCircle: true,
      index: null,
      geoKey: c.key,
      // Fail safe: a centre with no explicit trust flag is treated as approximate
      // (the resolver stamps false only for confirmed cases — country/coords/disambiguated).
      approximate: center.approximate ?? true,
    });
  }

  for (const p of geo.places) {
    const center = centers[geoCacheKey({ type: 'place', key: p.key })];
    if (!center) continue;
    const unit = p.distanceUnit === 'mile' ? 'mile' : 'kilometer';
    out.push({
      id: `${pfx}place-${p.key}`,
      kind: 'place',
      latitude: center.latitude,
      longitude: center.longitude,
      radius: p.radius > 0 ? p.radius : defaultPlaceRadius(unit),
      distanceUnit: unit,
      label: p.name,
      excluded,
      draggable: false,
      editableRadius: true,
      drawCircle: true,
      index: null,
      geoKey: p.key,
      approximate: center.approximate ?? true,
    });
  }

  for (const n of geo.neighborhoods) {
    const center = centers[geoCacheKey({ type: 'neighborhood', key: n.key })];
    if (!center) continue;
    out.push({
      id: `${pfx}nbhd-${n.key}`,
      kind: 'neighborhood',
      latitude: center.latitude,
      longitude: center.longitude,
      radius: 0,
      distanceUnit: 'kilometer',
      label: n.name,
      excluded,
      draggable: false,
      editableRadius: false,
      drawCircle: false, // pin only — Meta shows neighborhoods without a circle
      index: null,
      geoKey: n.key,
      approximate: center.approximate ?? true,
    });
  }

  return out;
}

// ---------- AREA boundaries (drawn outlines, not points) ----------
//
// country + region/state → Google Data-Driven Styling (DDS) boundaries, highlighted by
// the resolved Google place ID on the matching FeatureLayer. AU postcodes → a fetched
// ABS POA GeoJSON polygon drawn on the Maps Data layer. Both are AREA targets with NO
// radius (whole-area, matching Meta). Pure: the map does the imperative drawing.

export type GeoBoundaryKind = 'country' | 'region' | 'zip';
export type GeoBoundaryFeatureType = 'COUNTRY' | 'ADMINISTRATIVE_AREA_LEVEL_1';

export type GeoBoundary = {
  id: string;
  kind: GeoBoundaryKind;
  label: string;
  excluded: boolean;
  // SAFETY — true when the AREA was located by geocoding the NAME (country/region) and
  // may not match the Meta key. AU postcodes are drawn from the authoritative ABS polygon
  // (the real postcode shape) → false. See GeoMapPoint.approximate.
  approximate: boolean;
  // DDS path (country/region): the Google place id + feature-layer type to highlight.
  placeId: string | null;
  featureType: GeoBoundaryFeatureType | null;
  // GeoJSON path (zip): the fetched ABS POA polygon (a GeoJSON Feature), else null.
  geojson: unknown | null;
  // Area bounds (DDS, from the geocode viewport) for map-fit; null for zip (fit from geojson).
  bounds: GeoBounds | null;
};

// Minimal resolved-centre shape the boundary builder needs (matches the resolver's
// GeoResolution structurally — avoids a lib→module import).
type ResolvedCenterLike = {
  latitude: number;
  longitude: number;
  placeId: string | null;
  bounds: GeoBounds | null;
  approximate?: boolean;
};

// Build the AREA boundaries to draw. countries are passed as { key=ISO code, name }
// (the caller maps the code→name). An entry whose centre/place-id didn't resolve (DDS)
// or whose postcode polygon hasn't loaded (zip) is OMITTED — graceful: it stays a chip,
// isn't drawn, never crashes. Pure.
export function geoBoundaries(
  geo: {
    countries: { key: string; name: string }[];
    regions: TargetingRegion[];
    zips: TargetingZip[];
  },
  centers: Record<string, ResolvedCenterLike>,
  zipPolygons: Record<string, unknown>,
  opts?: { excluded?: boolean },
): GeoBoundary[] {
  const out: GeoBoundary[] = [];
  // Excluded areas draw red + carry an 'exc-' id prefix (an excluded region and an
  // included region with the same key must not collide on identity). Countries are
  // never excludable, so geo.countries is normally empty on the excluded pass.
  const excluded = opts?.excluded === true;
  const pfx = excluded ? 'exc-' : '';

  for (const c of geo.countries) {
    const r = centers[geoCacheKey({ type: 'country', key: c.key })];
    if (!r?.placeId) continue; // unresolved → not drawn (DDS needs the place id)
    out.push({
      id: `${pfx}country-${c.key}`,
      kind: 'country',
      label: c.name,
      excluded,
      placeId: r.placeId,
      featureType: 'COUNTRY',
      geojson: null,
      bounds: r.bounds ?? null,
      approximate: r.approximate ?? true,
    });
  }

  for (const rg of geo.regions) {
    const r = centers[geoCacheKey({ type: 'region', key: rg.key })];
    if (!r?.placeId) continue;
    out.push({
      id: `${pfx}region-${rg.key}`,
      kind: 'region',
      label: rg.name,
      excluded,
      placeId: r.placeId,
      featureType: 'ADMINISTRATIVE_AREA_LEVEL_1',
      geojson: null,
      bounds: r.bounds ?? null,
      approximate: r.approximate ?? true,
    });
  }

  for (const z of geo.zips) {
    const poly = zipPolygons[z.name]; // keyed by the postcode string (= zip.name)
    if (!poly) continue; // no polygon loaded (or not AU / NZ / unmatched) → not drawn
    out.push({
      id: `${pfx}zip-${z.key}`,
      kind: 'zip',
      label: z.name,
      excluded,
      placeId: null,
      featureType: null,
      geojson: poly,
      bounds: null,
      approximate: false, // the ABS POA polygon is the real postcode shape (authoritative)
    });
  }

  return out;
}

// Which boundary (if any) a DDS feature corresponds to — matched by feature type +
// Google place id. Pure (the map turns the match into a fill/stroke style). Returns
// null for an unmatched feature so the style function leaves it unstyled.
export function matchBoundaryForFeature(
  boundaries: GeoBoundary[],
  featureType: GeoBoundaryFeatureType,
  placeId: string | null | undefined,
): GeoBoundary | null {
  if (!placeId) return null;
  return boundaries.find((b) => b.featureType === featureType && b.placeId === placeId) ?? null;
}
